import { useMutation, useQueryClient } from '@tanstack/react-query'

import { approvalKeys, approveRequest, rejectRequest } from '@/api/approvals-api'
import type { ApiError } from '@/lib/problem-details'

/**
 * The two decisions.
 *
 * Both answer 204 and nothing else, so there is no body to write into a cache — and nothing that
 * could be written would be trustworthy anyway. An approval re-dispatches the maker's stored
 * command through the full pipeline, which may change a loan, a till, and a ledger period at once;
 * the honest response to that is to re-read, not to patch a row.
 *
 * The whole `approvals` root is invalidated rather than one key. A decision removes the request
 * from the queue *and* appends to its trail, and those are the two things on screen.
 */

export interface ApprovalDecision {
  requestId: string
  /** Optional on the wire; this client asks for one. Trimmed, and capped at the server's 1000. */
  note: string
}

export function useApproveRequest() {
  const queryClient = useQueryClient()

  return useMutation<void, ApiError, ApprovalDecision>({
    mutationFn: ({ requestId, note }) =>
      approveRequest(requestId, { note: note.trim() === '' ? null : note.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.all })
    },
  })
}

export interface ApprovalRefusal {
  requestId: string
  /** Mandatory, minimum five characters — the server refuses `access.reason_required` otherwise. */
  reason: string
}

export function useRejectRequest() {
  const queryClient = useQueryClient()

  return useMutation<void, ApiError, ApprovalRefusal>({
    mutationFn: ({ requestId, reason }) =>
      rejectRequest(requestId, { reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.all })
    },
  })
}
